import { LookupBsbDocument, PayIdType } from "@/api/generated/graphql";
import { PaymentType } from "@/api/paymentApi";
import { PaymentScheduleType } from "@/types/scheduledPaymentsTypes";
import { defaultCountryIso2 } from "@/utils/constants";
import { useIntl } from "@moroku/money-webapp-components";
import * as Sentry from "@sentry/vue";
import { useApolloClient } from "@vue/apollo-composable";
import { helpers } from "@vuelidate/validators";
import { validate as validateEmail } from "email-validator";
import { parsePhoneNumberWithError } from "libphonenumber-js";

const { withAsync } = helpers;

const { t } = useIntl();

export const nameValidator = () =>
  helpers.withMessage(t("validation_required"), (value, siblings) => {
    return siblings.paymentType !== PaymentType.BankTransfer || value.length > 0;
  });

export const payIdIdentifierValidator = () =>
  helpers.withMessage(t("validation_payId"), (value, siblings) => {
    // required validator tests if empty
    if (!value) {
      return true;
    }

    switch (siblings.type) {
      case PayIdType.Email:
        return validateEmail(value);
      case PayIdType.Phone:
        return parsePhoneNumberWithError(value, defaultCountryIso2).isValid();
      default:
        return true;
    }
  });

export const verifiedPayIdIdentifierValidator = () =>
  helpers.withMessage(t("payment_payId_notFound_additionalInfo"), (identifier, siblings) => {
    if (!identifier || identifier !== siblings.lastQueriedIdentifier) {
      return true;
    }

    if (siblings.lastQueriedIdentifier.length === 0) {
      return true;
    }

    return identifier === siblings.verifiedPayIdIdentifier;
  });

// Whether the user has pressed the Verify PayId button
export const queriedPayIdValidator = () =>
  helpers.withMessage(t("payment_payId_verification_required"), (value, siblings) => {
    return (
      (siblings.verifiedPayIdIdentifier.length > 0 && siblings.identifier === siblings.verifiedPayIdIdentifier) ||
      (siblings.lastQueriedIdentifier.length > 0 && siblings.identifier === siblings.lastQueriedIdentifier)
    );
  });

export const requiredIfScheduleType = scheduleTypes =>
  helpers.withMessage(t("validation_required"), (value, siblings) => {
    if (!scheduleTypes.includes(siblings.scheduleType)) return true;

    return !!value;
  });

// Assumes PaymentScheduleType.Recurring
export const requiredIfEndType = endType =>
  helpers.withMessage(t("validation_required"), (value, siblings) => {
    if (endType !== siblings.endType || siblings.scheduleType !== PaymentScheduleType.Recurring) return true;

    return !!value;
  });

// Assumes PaymentScheduleType.Recurring
export const integerIfEndType = endType =>
  helpers.withMessage(t("validation_integer"), (value, siblings) => {
    if (!helpers.req(value) || endType !== siblings.endType || siblings.scheduleType !== PaymentScheduleType.Recurring) return true;

    return Number.isInteger(value);
  });

// Assumes PaymentScheduleType.Recurring
export const minValueIfEndType = (minValue, endType) =>
  helpers.withMessage(t("validation_minimumOf", { minValue }), (value, siblings) => {
    if (!helpers.req(value) || endType !== siblings.endType || siblings.scheduleType !== PaymentScheduleType.Recurring) return true;

    return value >= minValue;
  });

// Assumes PaymentScheduleType.Recurring
export const endDateAfterStartDateIfEndType = endType =>
  helpers.withMessage(t("validation_endDateAfterStartDate"), (value, siblings) => {
    if (
      !helpers.req(value) ||
      siblings.startDate == null ||
      endType !== siblings.endType ||
      siblings.scheduleType !== PaymentScheduleType.Recurring
    )
      return true;

    return siblings.startDate < value;
  });

export const verifiedBpayBillerValidator = () =>
  helpers.withMessage(t("payment_bpayBiller_notFound_additionalInfo"), (billerCode, siblings) => {
    if (!billerCode || billerCode !== siblings.lastQueriedBillerCode) {
      return true;
    }

    if (siblings.lastQueriedBillerCode.length === 0) {
      return true;
    }

    return billerCode === siblings.verifiedBillerCode;
  });

// Whether the user has pressed the Verify Biller Code button
export const queriedBpayBillerValidator = () =>
  helpers.withMessage(t("payment_bpayBiller_verification_required"), (value, siblings) => {
    return (
      (siblings.verifiedBillerCode.length > 0 && siblings.billerCode === siblings.verifiedBillerCode) ||
      (siblings.lastQueriedBillerCode.length > 0 && siblings.billerCode === siblings.lastQueriedBillerCode)
    );
  });

export const verifiedBsbValidator = () =>
  helpers.withMessage(
    t("validation_bsbNotFound"),
    withAsync(async value => {
      if (!value || value.length !== 7) {
        return true;
      }

      try {
        const { resolveClient } = useApolloClient();
        const client = resolveClient();
        const result = await client.query({ query: LookupBsbDocument, variables: { bsbNumber: value }, fetchPolicy: "network-only" });

        return !!result.data?.lookupBsb;
      } catch (e) {
        Sentry.captureException(e);
        return false;
      }
    }),
  );
